<template>
  <TransitionGroup name="ef-toast" tag="div" class="toast-container top-0 end-0 p-3 h-100 overflow-hidden">
    <div
      v-for="toast in toasterStore.toasts"
      :key="toast.id"
      class="toast show ef-toast"
      :class="`ef-toast-${toneOf(toast)}`"
      :role="isUrgent(toast) ? 'alert' : 'status'"
      :aria-live="isUrgent(toast) ? 'assertive' : 'polite'"
      aria-atomic="true"
    >
      <!-- One row: the status in the icon, the text beside it, the close button last -->
      <div class="d-flex align-items-start gap-2">
        <span v-if="toast.icon" class="ef-toast-icon" aria-hidden="true">
          <i :class="`fa fa-fw ${toast.icon}`" />
        </span>
        <div class="flex-grow-1 pt-1" style="min-width: 0">
          <div class="ef-toast-title">{{ toast.title }}</div>
          <div v-if="toast.message" class="ef-toast-message">{{ toast.message }}</div>
        </div>
        <button
          type="button"
          class="btn btn-sm ef-toast-close"
          :aria-label="t('buttons.close')"
          :title="t('buttons.close')"
          @click="toasterStore.removeToast(toast.id)"
        >
          <i class="fa fa-fw fa-xmark" />
        </button>
      </div>
    </div>
  </TransitionGroup>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import useToasterStore from './toasts';

const { t } = useI18n();
const toasterStore = useToasterStore();

// The callers pass a text and a background colour (success on light, light on success, dark on
// warning); the status is whichever of the two is not a neutral one.
const NEUTRAL = ['light', 'dark', 'white', 'body'];

function toneOf(toast: { text_type?: string; bg_type?: string }): string {
	return [toast.bg_type, toast.text_type].find((color) => color && !NEUTRAL.includes(color)) ?? 'secondary';
}

// A failure or a warning interrupts a screen reader; a success waits until it has finished speaking
function isUrgent(toast: { text_type?: string; bg_type?: string }): boolean {
	return ['danger', 'warning'].includes(toneOf(toast));
}
</script>

<style lang="scss">
@import '../../sass/base';

// A neutral surface for every status: the colour sits in the icon only
.ef-toast {
	--ef-toast-tone-color: var(--bs-secondary-text-emphasis);

	max-width: 360px;
	margin-left: auto;
	padding: 0.6rem 0.5rem 0.6rem 0.6rem;
	background-color: var(--bs-body-bg);
}

// The status colour itself, about 4.5:1 on white; yellow and cyan only with black mixed in.
// The dark theme takes the light tints Bootstrap made for it.
@each $tone in (primary, secondary, success, info, warning, danger) {
	.ef-toast-#{$tone} {
		--ef-toast-tone-color: var(--bs-#{$tone});
	}

	[data-bs-theme='dark'] .ef-toast-#{$tone} {
		--ef-toast-tone-color: var(--bs-#{$tone}-text-emphasis);
	}
}

.ef-toast-warning {
	--ef-toast-tone-color: color-mix(in srgb, var(--bs-warning) 60%, #000);
}

.ef-toast-info {
	--ef-toast-tone-color: color-mix(in srgb, var(--bs-info) 60%, #000);
}

.ef-toast-icon {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	font-size: 1.25rem;
	color: var(--ef-toast-tone-color);
}

.ef-toast-title {
	font-weight: 600;
	line-height: 1.3;
	color: var(--bs-emphasis-color);
}

.ef-toast-message {
	margin-top: 0.1rem;
	font-size: 0.875em;
	line-height: 1.35;
	color: var(--bs-secondary-color);
}

.ef-toast-close {
	flex-shrink: 0;
	color: var(--bs-secondary-color);
	border: 0;

	&:hover,
	&:focus-visible {
		color: var(--bs-emphasis-color);
		background-color: var(--bs-tertiary-bg);
	}
}

// In from the right, out to the right; no movement for who asked for less
.ef-toast-enter-active,
.ef-toast-leave-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}

.ef-toast-enter-from,
.ef-toast-leave-to {
	opacity: 0;
	transform: translateX(1.5rem);
}

@media (prefers-reduced-motion: reduce) {
	.ef-toast-enter-active,
	.ef-toast-leave-active {
		transition: opacity 0.2s ease;
	}

	.ef-toast-enter-from,
	.ef-toast-leave-to {
		transform: none;
	}
}
</style>
